import { ConnectError } from '@bufbuild/connect';
import { createAsyncThunk } from '@reduxjs/toolkit';

import api from '@/shared/api/api.ts';
import { AlbumService } from '@/shared/api/protocol-ts/api/core/svc_album_connectweb.ts';
import {
  CreateAlbumReq,
  CreateAlbumResp,
  DeleteAlbumReq,
  DeleteAlbumResp,
  DeleteStudiesFromAlbumReq,
  DeleteStudiesFromAlbumResp,
  MovePhotosToAlbumReq,
  MovePhotosToAlbumResp,
  ReorderAlbumAssetsReq,
  ReorderAlbumAssetsResp,
  SetMainInfoReq,
  SetMainInfoResp,
} from '@/shared/api/protocol-ts/api/core/svc_album_pb';
import { SliceName } from '@/shared/config';
import { createThunkGenerator } from '@/shared/lib';

import type { PersistedPhotoTransform } from '../config';
import { mapTransformToViewOptions } from '../lib/mapTransformToViewOptions';

type AlbumRequests = keyof typeof AlbumService.methods;

const generateAlbumThunk = createThunkGenerator<AlbumRequests>(SliceName.album);

export const createAlbum = generateAlbumThunk<CreateAlbumReq, CreateAlbumResp>(
  'createAlbum',
  api.album.createAlbum,
);

export const deleteStudiesFromAlbum = generateAlbumThunk<
  DeleteStudiesFromAlbumReq,
  DeleteStudiesFromAlbumResp
>('deleteStudiesFromAlbum', api.album.deleteStudiesFromAlbum);

export const editAlbum = generateAlbumThunk<SetMainInfoReq, SetMainInfoResp>(
  'setMainInfo',
  api.album.setMainInfo,
);

export const deleteAlbum = generateAlbumThunk<DeleteAlbumReq, DeleteAlbumResp>(
  'deleteAlbum',
  api.album.deleteAlbum,
);

export const reorderAlbumAssets = generateAlbumThunk<
  ReorderAlbumAssetsReq,
  ReorderAlbumAssetsResp
>('reorderAlbumAssets', api.album.reorderAlbumAssets);

export const movePhotosToAlbum = generateAlbumThunk<
  MovePhotosToAlbumReq,
  MovePhotosToAlbumResp
>('movePhotosToAlbum', api.album.movePhotosToAlbum);

type SetPhotoViewOptionsArgs = {
  assetID: string;
  transform: PersistedPhotoTransform;
};

export const setPhotoViewOptions = createAsyncThunk(
  `${SliceName.album}/setPhotoViewOptions`,
  async (
    { assetID, transform }: SetPhotoViewOptionsArgs,
    { rejectWithValue },
  ) => {
    try {
      const response = await api.assets.setMedicalImageViewOptions({
        AssetID: assetID,
        MedicalImageViewOptionsV2: mapTransformToViewOptions(transform),
      });

      return response;
    } catch (error) {
      return rejectWithValue(ConnectError.from(error));
    }
  },
);

export const clearPhotoViewOptions = createAsyncThunk(
  `${SliceName.album}/clearPhotoViewOptions`,
  async (assetID: string, { rejectWithValue }) => {
    try {
      const response = await api.assets.setMedicalImageViewOptions({
        AssetID: assetID,
        MedicalImageViewOptionsV2: {
          Rotation: 0,
          FlipHorizontal: false,
          Zoom: 1,
          PanX: 0,
          PanY: 0,
        },
      });

      return response;
    } catch (error) {
      return rejectWithValue(ConnectError.from(error));
    }
  },
);
